import React, { useState, useEffect } from "react";
import Layout from '../../components/Layout';
import { get, post } from '../../services/ApiService';
import "../custom-style.css";
import Loader from "../../components/Loader";
import PostageTable from "./PostageTable";
import Pagination from '../../components/Pagination';
import toast, { Toaster } from 'react-hot-toast';
import Breadcrumbs from "../../components/Breadcrumbs";

const PostageList = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isNotFound, setNotFound] = useState(false); 
  const [postages, setPostages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(100);
  const [filters, setFilters] = useState({
    name: '',
  });
  
  // Postage types for the select dropdown
  const postageTypes = [
    { value: 'Ebay', label: 'Ebay' },
    { value: 'GroupOn', label: 'GroupOn' },
    { value: 'Amazon', label: 'Amazon' }
  ];

  // Modal state
  const [modal, setModal] = useState({
    show: false,
    mode: 'add', // 'add' or 'edit'
    data: null,
    name: '',
    type: 'Ebay'
  });

  useEffect(() => {
    fetchData();
  }, [currentPage, filters]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const Body = {
        page: currentPage,
        limit: limit,
        ...filters
      };
      const res = await post(`api/products/postages/list`, Body);
      console.log(res);
      if (res.success) {
        setIsLoading(false);
        setPostages(res.postages);
        setTotalPages(res.totalPages);
        setNotFound(false);
      } else {
        setPostages([]);
        setTotalPages(1);
        setIsLoading(false);
        setNotFound(true);
      }
    } catch (error) {
      setPostages([]);
      setTotalPages(1);
      setIsLoading(false);
      setNotFound(true);
      console.error(error.message);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  // Open modal for adding new postage
  const openAddModal = () => {
    setModal({
      show: true,
      mode: 'add',
      data: null,
      name: '',
      type: 'standard'
    });
  };

  // Open modal for editing existing postage
  const openEditModal = (postage) => {
    setModal({
      show: true,
      mode: 'edit',
      data: postage,
      name: postage.name,
      type: postage.type || 'standard'
    });
  };

  // Handle changes in modal form
  const handleModalChange = (e) => {
    setModal({
      ...modal,
      [e.target.name]: e.target.value
    });
  };

  // Submit the form (both add and edit)
  const handleModalSubmit = async () => {
    try {
      setIsSubmit(true);
      let res;
      if(!modal.name || !modal.type){
        toast.error('All fields are required');
        return
      }
      if (modal.mode === 'add') {
        res = await post(`api/products/postages/create`, {
          name: modal.name,
          type: modal.type
        });
      } else {
        res = await post(`api/products/postages/update`, {
          id: modal.data._id,
          name: modal.name,
          type: modal.type
        });
      }
      
      if (res.success) {
        toast.success(res.message || (modal.mode === 'add' ? 'Postage added successfully' : 'Postage updated successfully'));
        fetchData(); // Refresh the data
        setModal({ ...modal, show: false });
      } else {
        toast.error(res.error || (modal.mode === 'add' ? 'Failed to add postage' : 'Failed to update postage'));
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
      console.error(error);
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <>
      <Layout>
        <Toaster 
          position="top-center"
          reverseOrder={false}
        />
        <div className="row mb-9 align-items-center justify-content-between">
          <div className="col-md-4 mb-8 mb-md-0">
            <h2 className="fs-4 mb-0">Postage List</h2>
            <Breadcrumbs />
          </div>
          <div className="col-md-8 d-flex flex-wrap justify-content-md-end">
            <button 
              className="btn btn-primary btn-sm"
              onClick={openAddModal}
            >
              Add New Postage
            </button>
          </div>
        </div>
        <div className="card mb-4 rounded-4 p-7">
          <div className="card-header bg-transparent px-0 pt-0 pb-7">
            <div className="row align-items-center justify-content-between">
              <div className="col-md-6 col-12 mr-auto mb-md-0 mb-6">
                <input 
                  type="text" 
                  name="name" 
                  placeholder="Search by name..." 
                  value={filters.name} 
                  onChange={handleFilterChange} 
                  className="form-control bg-input border-0" 
                />
              </div>
            </div>
          </div>
          <div className="card-body px-0 pt-7 pb-0">
            {isLoading ? (
              <div className='text-center'>
                <Loader />
              </div>
            ) : ( 
              <div className="table-responsive">
                <PostageTable 
                  postages={postages} 
                  onEdit={openEditModal} 
                />
              </div>
            )}

            {!isLoading && isNotFound && 
              <div className="text-center">
                <img src="/assets/images/no-data.png" style={{width: '60px', opacity: '0.5'}} alt="No data" />
                <h6 style={{color: '#918f8f', fontFamily: 'math', fontWeight: '500'}}>No Data Found</h6>
              </div>
            }
          </div>
        </div>
        {totalPages && totalPages > 1 && 
          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={handlePageChange} 
          />
        }

        {/* Add/Edit Postage Modal */}
        <div className={`modal fade ${modal.show ? 'show' : ''}`} 
          style={{display: modal.show ? 'block' : 'none'}} 
          tabIndex="-1" 
          aria-labelledby="postageModalLabel" 
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h1 className="modal-title fs-5" id="postageModalLabel">
                  {modal.mode === 'add' ? 'Add New Postage' : 'Edit Postage'}
                </h1>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setModal({ ...modal, show: false })} 
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="postageName" className="form-label">Name</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    id="postageName" 
                    name="name"
                    value={modal.name} 
                    onChange={handleModalChange}
                    placeholder="Enter postage name"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="postageType" className="form-label">Type</label>
                  <select
                    className="form-select"
                    id="postageType"
                    name="type"
                    value={modal.type}
                    onChange={handleModalChange}
                  >
                    {postageTypes.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setModal({ ...modal, show: false })}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleModalSubmit} 
                  disabled={isSubmit}
                >
                  {isSubmit && <i className="fa fa-spinner fa-spin"></i>}
                  {modal.mode === 'add' ? 'Add Postage' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
        {modal.show && <div className="modal-backdrop fade show"></div>}
      </Layout>
    </>
  );
};

export default PostageList;